
import type { ExtendedEmailSettings } from "@/integrations/supabase/client";

export interface Profile {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email?: string;
}

export interface ExpenseReport {
  id: string;
  organization_id: string;
  generated_by: string;
  approved_by: string | null;
  date_from: string;
  date_to: string;
  total_amount: number;
  status: string;
  created_at: string;
  approval_date: string | null;
  expenses_posted: boolean;
  currency: string;
  email_sent_at: string | null;
  email_sent_to: string | null;
  rejection_comments?: string | null;
}

export interface ExpenseItem {
  id: string;
  expense_report_id: string;
  organization_id: string;
  created_by: string;
  created_at: string;
  description: string;
  amount: number;
  date: string;
  category_id: number;
  categories: {
    name: string;
  } | null;
  currency: string;
  receipt_url: string | null;
  status: string;
}

export interface Category {
  id: number;
  name: string;
  organization_id: string;
}

export interface Receipt {
  expense_id: number;
  file_url: string;
  file_name: string;
}

export const CURRENCIES = [
  { code: "USD", symbol: "$" },
  { code: "CAD", symbol: "CA$" },
  { code: "EUR", symbol: "€" },
  { code: "GBP", symbol: "£" },
];

// Helper functions for consistent date handling
export const formatDateForStorage = (date: Date): string => {
  // Ensure we're working with UTC to avoid timezone issues
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const displayDate = (dateString: string): string => {
  // Handle date display consistently to avoid timezone issues
  // Create a date object without timezone conversion
  const [year, month, day] = dateString.split('-').map(num => parseInt(num, 10));
  const date = new Date(year, month - 1, day);
  
  return new Intl.DateTimeFormat('en-US', { 
    month: 'short', 
    day: 'numeric', 
    year: 'numeric' 
  }).format(date);
};

// This function ensures that dates are properly converted for HTML input fields
// which expect dates in YYYY-MM-DD format
export const formatDateForInput = (dateString: string): string => {
  // For input fields, we need YYYY-MM-DD format
  // First parse the date without timezone conversion
  const [year, month, day] = dateString.split('-').map(num => parseInt(num, 10));
  if (!year || !month || !day) {
    // If we can't parse the date string, return today's date
    return formatDateForStorage(new Date());
  }
  
  // Create a new date with the parsed components (local time)
  const date = new Date(year, month - 1, day);
  return formatDateForStorage(date);
};
